import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { siteData } from "@/store/siteData";
import type { AdItemServer } from "@/store/siteData";

// Re-export AdItem as the server-backed type
export type AdItem = AdItemServer;

// ── Ad Settings (per-site, stored server-side via siteData) ──────────────────

const AD_SETTINGS_KEY = "yg_ad_settings";
const AD_LAST_SHOWN_KEY = "yg_ad_last_shown";

export interface AdSettings {
  showDurationHours: number;
}

// Settings stored in siteData (server) for cross-platform sync
export function getAdSettings(): AdSettings {
  try {
    const fromServer = siteData.getAdSettings();
    if (fromServer) return fromServer;
  } catch { /**/ }
  try {
    const raw = localStorage.getItem(AD_SETTINGS_KEY);
    return raw ? JSON.parse(raw) : { showDurationHours: 24 };
  } catch {
    return { showDurationHours: 24 };
  }
}

export async function saveAdSettings(s: AdSettings): Promise<void> {
  localStorage.setItem(AD_SETTINGS_KEY, JSON.stringify(s));
  try {
    await siteData.saveAdSettings(s);
  } catch { /**/ }
}

// ── Ad Metadata (server-backed) ───────────────────────────────────────────────

export function getAds(): AdItem[] {
  return siteData.getAds();
}

export async function saveAds(ads: AdItem[]): Promise<void> {
  await siteData.saveAds(ads);
}

// ── Admin paths (no ads here) ─────────────────────────────────────────────────

const ADMIN_PATHS = ["/kontrol-paneli", "/yapi/sistem/gelistirici-x9f2"];

// ── Per-visitor last shown tracking (localStorage, per-device intentional) ────

function shouldShowAd(): boolean {
  const settings = getAdSettings();
  const lastShown = localStorage.getItem(AD_LAST_SHOWN_KEY);
  if (!lastShown) return true;
  const elapsed = Date.now() - parseInt(lastShown, 10);
  return elapsed > settings.showDurationHours * 3600 * 1000;
}

function markAdShown(): void {
  localStorage.setItem(AD_LAST_SHOWN_KEY, String(Date.now()));
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function InterstitialAd() {
  const [visible, setVisible] = useState(false);
  const [adImageUrl, setAdImageUrl] = useState<string | null>(null);
  const [adLink, setAdLink] = useState("");
  const [animateIn, setAnimateIn] = useState(false);
  const { pathname } = useLocation();
  const [cacheReady, setCacheReady] = useState(false);

  // Cache değişimlerini dinle — sayfa açılışında ve kontrol panelinden güncelleme yapıldığında
  useEffect(() => {
    const unsub = siteData.subscribe(() => {
      setCacheReady(true);
    });
    
    // Eğer cache zaten yüklüyse beklemede kalmaması için load çağırıp hazır olarak işaretliyoruz
    siteData.load().then(() => {
      setCacheReady(true);
    });

    return unsub;
  }, []);

  // Cache hazır olana kadar bekle, hazır olunca reklamı kontrol et
  useEffect(() => {
    if (!cacheReady) return;
    // Admin sayfalarında reklam gösterme
    if (ADMIN_PATHS.some(p => pathname.startsWith(p))) return;
    if (!shouldShowAd()) return;

    const ads = siteData.getAds();
    const activeAd = ads.find(a => a.active);
    if (!activeAd?.imageUrl) return;
    setAdImageUrl(activeAd.imageUrl);
    setAdLink(activeAd.link || "");
    setVisible(true);
    setTimeout(() => setAnimateIn(true), 50);
  }, [cacheReady, pathname]);



  const handleClose = () => {
    setAnimateIn(false);
    markAdShown();
    setTimeout(() => setVisible(false), 300);
  };

  if (!visible || !adImageUrl) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center"
      style={{
        background: animateIn ? "rgba(0,0,0,0.75)" : "rgba(0,0,0,0)",
        backdropFilter: animateIn ? "blur(4px)" : "none",
        transition: "background 0.3s ease, backdrop-filter 0.3s ease",
      }}
    >
      <div
        style={{
          transform: animateIn ? "scale(1) translateY(0)" : "scale(0.9) translateY(20px)",
          opacity: animateIn ? 1 : 0,
          transition: "transform 0.3s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease",
          position: "relative",
          maxWidth: "min(90vw, 600px)",
          maxHeight: "min(90vh, 700px)",
        }}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute -top-4 -right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors"
          style={{ fontSize: "20px", fontWeight: "bold", lineHeight: 1, boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}
          title="Kapat"
        >
          ✕
        </button>

        {/* Ad image */}
        <div className="relative rounded-2xl overflow-hidden" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.5)" }}>
          {adLink ? (
            <a href={adLink} target="_blank" rel="noopener noreferrer" onClick={handleClose} className="block cursor-pointer">
              <img
                src={adImageUrl}
                alt="Reklam"
                style={{ display: "block", maxWidth: "min(90vw, 600px)", maxHeight: "min(80vh, 600px)", objectFit: "contain" }}
              />
            </a>
          ) : (
            <img
              src={adImageUrl}
              alt="Reklam"
              style={{ display: "block", maxWidth: "min(90vw, 600px)", maxHeight: "min(80vh, 600px)", objectFit: "contain" }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
